import { Fragment, useState } from 'react';
import clsx from 'clsx';
import styles from './ReconTable.module.scss';
import DrillDownRows from './DrillDownRows';
import ReconCellContent, { cellBandClass } from './ReconCellContent';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import { caretRight, infoToolTip } from '../../../../../assets/hostedassets';
import { COLUMN_TOOLTIPS, columnMetaCaveat, PROVISIONAL_FY_TITLE_SUFFIX } from '../constants/reconciliationsCopy';
import { columnOperator } from '../constants/reconciliationsColumnRules';
import { formatAmountWithUnit, useUnits } from '../UnitsContext';
import type { Recon, ReconColumn } from '../../../../../store/reconciliations/types';

interface ReconTableProps {
    orgId: string;
    fys: number[] | null;
    recon: Recon;
    /** The recon header's currently selected toggle option (ReconRow owns the state) — null on a
     *  recon with no toggle. Only ever used to resolve which columns show; the toggle-owned columns
     *  themselves already carry their own badge/value on the wire, so this never refetches.
     *  Optional (defaults to null) so a recon with no toggle can render without a caller ever
     *  passing one. */
    activeToggleOptionId?: string | null;
}

// The columns actually rendered: every wire column, minus any column owned by a toggle option that
// ISN'T the active one (a recon whose options carry no `columnKeys` hides nothing here, so an older
// server payload renders exactly as before).
function visibleColumns(recon: Recon, activeToggleOptionId: string | null): ReconColumn[] {
    const toggleOwnedInactiveKeys = new Set(
        (recon.sourceToggle?.options ?? [])
            .filter((option) => option.id !== activeToggleOptionId)
            .flatMap((option) => option.columnKeys ?? []),
    );
    return recon.columns.filter((column) => !toggleOwnedInactiveKeys.has(column.key));
}

function ReconTable({ orgId, fys, recon, activeToggleOptionId = null }: Readonly<ReconTableProps>) {
    const [expandedFy, setExpandedFy] = useState<number | null>(null);
    const { unit } = useUnits();
    const formatAmount = (value: number) => formatAmountWithUnit(value, unit);
    const columns = visibleColumns(recon, activeToggleOptionId);

    // Wire order is latest-first; the drawer shows oldest-first. Render order only.
    const displayRows = [...recon.rows].sort((a, b) => a.fy - b.fy);
    const hasExpandableRows = displayRows.some((row) => row.drillDownAvailable);

    return (
        <div className={styles.TableWrapper}>
            <table className={clsx(styles.ReconTable, { [styles.ReconTableExpandable]: hasExpandableRows })}>
                <thead>
                    <tr>
                        <th className={clsx(styles.HeaderCell, styles.FyHeaderCell)}>Financial Year</th>
                        {columns.map((column) => {
                            const operator = columnOperator(recon.id, column.key);
                            return (
                                <th key={column.key} className={styles.HeaderCell}>
                                    {operator && (
                                        <span className={styles.HeaderOperator} aria-hidden="true">
                                            {operator}
                                        </span>
                                    )}
                                    <span className={styles.HeaderCellInner}>
                                        {column.label}
                                        <Tooltip content={COLUMN_TOOLTIPS[column.key] ?? column.label} direction="top" usePortal fitContent>
                                            <img src={infoToolTip} height="14px" alt="" className={styles.InfoIcon} />
                                        </Tooltip>
                                    </span>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {displayRows.map((row) => {
                        const isExpanded = expandedFy === row.fy;
                        const canDrillDown = row.drillDownAvailable;
                        const toggleExpand = () => setExpandedFy(isExpanded ? null : row.fy);
                        return (
                            <Fragment key={row.fy}>
                                <tr className={styles.FyRow}>
                                    <td
                                        className={styles.FyLabelCell}
                                        title={row.meta?.isProvisional ? `${row.fyLabel}${PROVISIONAL_FY_TITLE_SUFFIX}` : row.fyLabel}
                                    >
                                        {canDrillDown && (
                                            <button
                                                type="button"
                                                aria-label={`Show breakup for ${row.fyLabel}`}
                                                aria-expanded={isExpanded}
                                                onClick={toggleExpand}
                                                className={clsx(styles.Chevron, { [styles.ChevronExpanded]: isExpanded })}
                                            >
                                                <img src={caretRight} alt="" />
                                            </button>
                                        )}
                                        {row.meta?.isProvisional ? (
                                            <span aria-label={`${row.fyLabel}${PROVISIONAL_FY_TITLE_SUFFIX}`}>{row.fyLabel}*</span>
                                        ) : (
                                            row.fyLabel
                                        )}
                                    </td>
                                    {columns.map((column) => {
                                        const caveat = columnMetaCaveat(column.key, row.meta, formatAmount);
                                        return (
                                            <td
                                                key={column.key}
                                                className={clsx(styles.Cell, cellBandClass(row.cells[column.key], column))}
                                            >
                                                <ReconCellContent
                                                    cell={row.cells[column.key]}
                                                    column={column}
                                                    unit={unit}
                                                    caveat={caveat}
                                                    provisional={row.meta?.provisionalKeys?.includes(column.key) ?? false}
                                                />
                                            </td>
                                        );
                                    })}
                                </tr>
                                {isExpanded && canDrillDown && (
                                    <DrillDownRows orgId={orgId} fys={fys} reconId={recon.id} fy={row.fy} columns={columns} />
                                )}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default ReconTable;
